<script setup>
/* eslint-disable vue/no-root-v-if */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePwaInstall } from 'dashboard/composables/usePwaInstall';
import { HOME_CARD_CLASS, HOME_PRIMARY_LINK_CLASS } from './homeTones';

const { t } = useI18n();
const appIconPath = '/talkvia-app-192.png';
const { isInstalled, isInstalling, platform, install } = usePwaInstall();
const showInstructions = ref(false);
const error = ref(false);
const animationPaused = ref(false);
const instructionSteps = computed(() => {
  const prefix = {
    ios: 'IOS',
    android: 'ANDROID',
    mac: 'MAC',
    desktop: 'DESKTOP',
  }[platform.value];
  return [1, 2, 3].map(step =>
    t(`OPERATIONAL_HOME.INSTALL.STEPS.${prefix}_${step}`)
  );
});
const benefits = computed(() => [
  {
    icon: 'i-hugeicons-touch-01',
    text: t('OPERATIONAL_HOME.INSTALL.QUICK_ACCESS'),
  },
  {
    icon: 'i-hugeicons-computer',
    text: t('OPERATIONAL_HOME.INSTALL.OWN_WINDOW'),
  },
  {
    icon: 'i-hugeicons-notification-02',
    text: t('OPERATIONAL_HOME.INSTALL.NOTIFICATIONS'),
  },
]);

const requestInstall = async () => {
  error.value = false;
  try {
    showInstructions.value = (await install()) === 'unavailable';
  } catch {
    error.value = true;
    showInstructions.value = true;
  }
};
</script>

<template>
  <section
    v-if="!isInstalled"
    :class="HOME_CARD_CLASS"
    class="install-talkvia-card relative isolate min-w-0 overflow-hidden p-5 md:p-6"
    :data-animation-paused="animationPaused"
    :aria-label="t('OPERATIONAL_HOME.INSTALL.TITLE')"
  >
    <div class="install-scene" aria-hidden="true">
      <div class="install-scene-glow" />
      <div class="install-mac">
        <div class="install-mac-screen">
          <div class="install-app-window">
            <span /><span /><span />
            <div class="install-progress"><i /></div>
          </div>
          <div class="install-dock">
            <i /><i /><img
              :src="appIconPath"
              alt=""
              width="32"
              height="32"
              class="install-dock-app"
            /><i />
          </div>
        </div>
        <div class="install-mac-base" />
      </div>
      <div class="install-phone">
        <div class="install-phone-notch" />
        <div class="install-phone-grid">
          <i /><i /><i /><i />
          <div class="install-phone-app">
            <img :src="appIconPath" alt="" width="38" height="38" /><svg
              class="install-ring"
              viewBox="0 0 44 44"
            >
              <circle cx="22" cy="22" r="20" />
            </svg>
          </div>
          <i />
        </div>
        <span class="install-phone-home" />
      </div>
      <span class="install-complete i-lucide-circle-check" />
    </div>
    <div class="relative z-10 flex flex-col gap-5 md:pr-80 lg:pr-96">
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-3">
          <img
            :src="appIconPath"
            alt=""
            class="size-12 shrink-0 rounded-xl"
            width="48"
            height="48"
          />
          <div class="min-w-0">
            <h2 class="mb-1 text-base font-semibold text-n-slate-12">
              {{ t('OPERATIONAL_HOME.INSTALL.TITLE') }}
            </h2>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ t('OPERATIONAL_HOME.INSTALL.DESCRIPTION') }}
            </p>
          </div>
        </div>
        <ul class="mt-4 mb-0 grid list-none gap-2 p-0 xl:grid-cols-3">
          <li
            v-for="benefit in benefits"
            :key="benefit.icon"
            class="flex min-w-0 items-start gap-2 text-sm text-n-slate-11"
          >
            <span
              :class="benefit.icon"
              class="mt-0.5 size-4 shrink-0"
              aria-hidden="true"
            />
            {{ benefit.text }}
          </li>
        </ul>
      </div>
      <div class="flex flex-wrap items-center gap-3">
        <button
          type="button"
          :class="HOME_PRIMARY_LINK_CLASS"
          :disabled="isInstalling"
          :aria-expanded="showInstructions"
          aria-controls="talkvia-install-instructions"
          @click="requestInstall"
        >
          <span class="i-hugeicons-download-04 size-4" aria-hidden="true" />
          {{ t('OPERATIONAL_HOME.INSTALL.BUTTON') }}
        </button>
        <p class="mb-0 max-w-60 text-xs text-n-slate-11">
          {{ t('OPERATIONAL_HOME.INSTALL.NOTIFICATION_NOTICE') }}
        </p>
      </div>
    </div>
    <p v-if="error" class="mt-4 mb-0 text-sm text-n-ruby-11" role="alert">
      {{ t('OPERATIONAL_HOME.INSTALL.ERROR') }}
    </p>
    <div
      v-if="showInstructions"
      id="talkvia-install-instructions"
      class="relative z-10 mt-5 rounded-xl bg-n-solid-2 p-4 text-sm text-n-slate-12"
      role="region"
      :aria-label="t('OPERATIONAL_HOME.INSTALL.HOW_TO')"
    >
      <div class="mb-2 flex items-center justify-between gap-3">
        <h3 class="mb-0 text-sm font-semibold">
          {{ t('OPERATIONAL_HOME.INSTALL.HOW_TO') }}
        </h3>
        <button
          type="button"
          class="relative grid size-8 shrink-0 place-items-center rounded-lg hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          :aria-label="t('OPERATIONAL_HOME.INSTALL.CLOSE')"
          @click="showInstructions = false"
        >
          <span class="i-hugeicons-cancel-01 size-4" aria-hidden="true" />
        </button>
      </div>
      <ol class="mb-0 grid list-decimal gap-2 pl-5">
        <li v-for="step in instructionSteps" :key="step">{{ step }}</li>
      </ol>
    </div>
    <button
      type="button"
      class="install-animation-control relative z-10 mt-3 inline-flex items-center gap-1.5 rounded-md text-xs text-n-slate-10 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
      :aria-pressed="animationPaused"
      @click="animationPaused = !animationPaused"
    >
      <span
        :class="animationPaused ? 'i-lucide-play' : 'i-lucide-pause'"
        class="size-3"
        aria-hidden="true"
      />
      {{
        t(
          animationPaused
            ? 'OPERATIONAL_HOME.INSTALL.RESUME_ANIMATION'
            : 'OPERATIONAL_HOME.INSTALL.PAUSE_ANIMATION'
        )
      }}
    </button>
  </section>
</template>

<style scoped>
.install-scene {
  position: absolute;
  right: -12px;
  bottom: 4px;
  width: 330px;
  height: 210px;
  opacity: 0.1;
  pointer-events: none;
}
.install-scene-glow {
  position: absolute;
  inset: -20px;
  border-radius: 50%;
  background: radial-gradient(
    ellipse,
    rgb(var(--blue-9) / 0.18),
    transparent 68%
  );
}
.install-mac {
  position: absolute;
  left: 0;
  top: 23px;
  width: 254px;
}
.install-mac-screen {
  height: 157px;
  overflow: hidden;
  border: 7px solid rgb(var(--slate-6));
  border-bottom-width: 12px;
  border-radius: 12px 12px 4px 4px;
  background: linear-gradient(135deg, rgb(var(--slate-3)), rgb(var(--slate-5)));
  box-shadow: 0 12px 24px rgb(0 0 0 / 0.08);
}
.install-mac-base {
  height: 9px;
  margin-inline: -13px;
  background: rgb(var(--slate-7));
  border-radius: 2px 2px 50% 50%;
}
.install-app-window {
  position: absolute;
  top: 22px;
  left: 50px;
  width: 148px;
  height: 74px;
  padding: 8px;
  border: 1px solid rgb(var(--slate-6));
  border-radius: 6px;
  background: rgb(var(--slate-2));
  animation: install-window 8s ease-in-out infinite;
}
.install-app-window > span {
  display: inline-block;
  width: 5px;
  height: 5px;
  margin-right: 4px;
  border-radius: 50%;
  background: rgb(var(--slate-7));
}
.install-progress {
  height: 5px;
  margin: 20px 14px 0;
  overflow: hidden;
  border-radius: 3px;
  background: rgb(var(--slate-5));
}
.install-progress i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: rgb(var(--blue-9));
  transform-origin: left;
  animation: install-progress 8s ease-in-out infinite;
}
.install-dock {
  position: absolute;
  top: 111px;
  left: 64px;
  display: flex;
  align-items: center;
  gap: 7px;
  height: 39px;
  padding: 5px 7px;
  border: 1px solid rgb(var(--slate-6));
  border-radius: 10px;
  background: rgb(var(--slate-2) / 0.85);
}
.install-dock i {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: rgb(var(--slate-6));
}
.install-dock-app {
  border-radius: 8px;
  animation: install-dock 8s ease-in-out infinite;
}
.install-phone {
  position: absolute;
  right: 8px;
  top: 6px;
  width: 90px;
  height: 184px;
  overflow: hidden;
  border: 5px solid rgb(var(--slate-8));
  border-radius: 20px;
  background: linear-gradient(155deg, rgb(var(--slate-4)), rgb(var(--slate-2)));
  box-shadow: 0 10px 24px rgb(0 0 0 / 0.12);
}
.install-phone-notch {
  width: 32px;
  height: 8px;
  margin: 5px auto 15px;
  border-radius: 8px;
  background: rgb(var(--slate-8));
}
.install-phone-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px 5px;
  padding: 0 8px;
}
.install-phone-grid > i {
  height: 18px;
  border-radius: 5px;
  background: rgb(var(--slate-6));
}
.install-phone-app {
  position: relative;
  grid-column: span 2;
  width: 38px;
  height: 38px;
  margin: 9px auto;
}
.install-phone-app img {
  border-radius: 10px;
  animation: install-phone-app 8s ease-in-out infinite;
}
.install-ring {
  position: absolute;
  inset: -3px;
  width: 44px;
  height: 44px;
  transform: rotate(-90deg);
  animation: install-ring-hide 8s ease-in-out infinite;
}
.install-ring circle {
  fill: none;
  stroke: rgb(var(--blue-9));
  stroke-width: 2;
  stroke-linecap: round;
  stroke-dasharray: 126;
  animation: install-ring 8s ease-in-out infinite;
}
.install-phone-home {
  position: absolute;
  bottom: 5px;
  left: 24px;
  width: 32px;
  height: 3px;
  border-radius: 3px;
  background: rgb(var(--slate-8));
}
.install-complete {
  position: absolute;
  right: 106px;
  bottom: 8px;
  width: 24px;
  height: 24px;
  color: rgb(var(--blue-9));
  animation: install-complete 8s ease-in-out infinite;
}
[data-animation-paused='true'] .install-scene * {
  animation-play-state: paused;
}
@keyframes install-progress {
  0%,
  8% {
    transform: scaleX(0);
  }
  50%,
  100% {
    transform: scaleX(1);
  }
}
@keyframes install-window {
  0%,
  45% {
    opacity: 1;
    transform: translateY(0);
  }
  60%,
  92% {
    opacity: 0;
    transform: translateY(-8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes install-dock {
  0%,
  35% {
    opacity: 0.35;
    transform: translateY(-14px) scale(0.7);
  }
  55% {
    opacity: 1;
    transform: translateY(-4px) scale(1.05);
  }
  68%,
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
@keyframes install-phone-app {
  0%,
  10% {
    opacity: 0.4;
    transform: scale(0.9);
  }
  60%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
@keyframes install-ring {
  0%,
  10% {
    stroke-dashoffset: 126;
  }
  60%,
  100% {
    stroke-dashoffset: 0;
  }
}
@keyframes install-ring-hide {
  0%,
  55% {
    opacity: 1;
  }
  65%,
  100% {
    opacity: 0;
  }
}
@keyframes install-complete {
  0%,
  58% {
    opacity: 0;
    transform: translateY(4px);
  }
  70%,
  92% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
  }
}
@media (min-width: 768px) {
  .install-scene {
    right: 18px;
    top: 50%;
    bottom: auto;
    transform: translateY(-50%);
    opacity: 0.9;
  }
}
@media (prefers-reduced-motion: reduce) {
  .install-animation-control {
    display: none;
  }
  .install-scene * {
    animation: none !important;
  }
  .install-app-window,
  .install-ring {
    display: none;
  }
  .install-complete,
  .install-dock-app,
  .install-phone-app img {
    opacity: 1;
    transform: none;
  }
}
</style>
